import React, { useRef, useState, useEffect, useCallback } from "react";
import SignatureCanvas from "react-signature-canvas";

const FONT_LINK_ID = "esig-fonts";
const GOOGLE_FONTS_URL =
  "https://fonts.googleapis.com/css2?family=Dancing+Script:wght@600&family=Great+Vibes&family=Caveat:wght@600&family=Pacifico&family=Pinyon+Script&display=swap";

function ensureFontsLoaded() {
  if (document.getElementById(FONT_LINK_ID)) return;
  const link = document.createElement("link");
  link.id = FONT_LINK_ID;
  link.rel = "stylesheet";
  link.href = GOOGLE_FONTS_URL;
  document.head.appendChild(link);
}

const SIGNATURE_FONTS = [
  { label: "Dancing Script", value: "'Dancing Script', cursive" },
  { label: "Great Vibes", value: "'Great Vibes', cursive" },
  { label: "Caveat", value: "'Caveat', cursive" },
  { label: "Pacifico", value: "'Pacifico', cursive" },
  { label: "Pinyon Script", value: "'Pinyon Script', cursive" },
];

function typedTextToFile(text: string, font: string): Promise<File> {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement("canvas");
    canvas.width = 480;
    canvas.height = 160;
    const ctx = canvas.getContext("2d");
    if (!ctx) return reject(new Error("Canvas not supported"));
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.font = `52px ${font}`;
    ctx.fillStyle = "#1a1a2e";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(text, canvas.width / 2, canvas.height / 2);
    canvas.toBlob((blob) => {
      if (!blob) return reject(new Error("toBlob failed"));
      resolve(new File([blob], "signature.png", { type: "image/png" }));
    }, "image/png");
  });
}

function dataUrlToFile(dataUrl: string): File {
  const [header, data] = dataUrl.split(",");
  const mime = header.match(/:(.*?);/)?.[1] ?? "image/png";
  const bytes = atob(data);
  const arr = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
  return new File([arr], "signature.png", { type: mime });
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface ESignaturePadProps<TField extends string = string> {
  field: TField; // ← key into formData
  onChange: (field: TField, file: File | null) => void;
  existingSignatureUrl?: string | null;
  error?: string;
  isDisabled?: boolean;
}

// ─── Component ────────────────────────────────────────────────────────────────

function ESignaturePad<TField extends string = string>({
  field,
  onChange,
  existingSignatureUrl,
  error,
  isDisabled = false,
}: ESignaturePadProps<TField>) {
  type Mode = "draw" | "type";
  const [mode, setMode] = useState<Mode>("draw");
  const [typedName, setTypedName] = useState("");
  const [selectedFont, setSelectedFont] = useState(SIGNATURE_FONTS[0].value);
  const [preview, setPreview] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const sigCanvasRef = useRef<SignatureCanvas>(null);

  useEffect(() => {
    ensureFontsLoaded();
  }, []);

  const clearSignature = useCallback(() => {
    sigCanvasRef.current?.clear();
    setTypedName("");
    setPreview(null);
    setConfirmed(false);
    onChange(field, null);
  }, [field, onChange]);

  const switchMode = (next: Mode) => {
    clearSignature();
    setMode(next);
  };

  const handleDrawEnd = () => {
    if (!sigCanvasRef.current || sigCanvasRef.current.isEmpty()) return;
    const dataUrl = sigCanvasRef.current.toDataURL("image/png");
    onChange(field, dataUrlToFile(dataUrl));
    setPreview(dataUrl);
    setConfirmed(true);
  };

  const handleApplyTyped = async () => {
    if (!typedName.trim()) return;
    const file = await typedTextToFile(typedName.trim(), selectedFont);
    const url = URL.createObjectURL(file);
    setPreview(url);
    setConfirmed(true);
    onChange(field, file);
  };

  useEffect(() => {
    if (isDisabled) {
      sigCanvasRef.current?.off();
    } else {
      sigCanvasRef.current?.on();
    }
  }, [isDisabled]);

  return (
    <div style={{ width: "100%", maxWidth: 520 }}>
      <style>
        {`
      .signature-canvas {
        width: 100% !important;
        height: 150px !important;
        display: block;
        touch-action: none;
      }

      @media (max-width: 576px) {
        .signature-canvas {
          height: 140px !important;
        }
      }
    `}
      </style>
      {/* Signature Tabs */}
      <div
        className="d-flex flex-wrap p-1 rounded-3 mb-3"
        style={{
          background: "#f1f3f5",
          border: "1px solid #e9ecef",
        }}
      >
        {(["draw", "type"] as Mode[]).map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => switchMode(m)}
            disabled={isDisabled}
            aria-pressed={mode === m}
            className="btn flex-fill d-flex align-items-center justify-content-center gap-2"
            style={{
              padding: "10px 12px",
              border: "none",
              borderRadius: 8,
              fontWeight: 600,
              fontSize: "0.88rem",
              background: mode === m ? "#fd7e14" : "transparent",
              color: mode === m ? "#fff" : "#495057",
              boxShadow: mode === m ? "0 2px 5px #00000012" : "none",
              opacity: isDisabled ? 0.6 : 1,
            }}
          >
            <i
              className={`ti ${m === "draw" ? "ti-pencil" : "ti-typography"}`}
            />
            {m === "draw" ? "Draw Signature" : "Type Signature"}
          </button>
        ))}
      </div>

      {/* DRAW SIGNATURE */}
      {mode === "draw" && !confirmed && (
        <div>
          <div
            className="rounded-3 p-2"
            style={{
              border: "1.5px dashed #ced4da",
              background: "#f8f9fa",
            }}
          >
            <div className="d-flex justify-content-between align-items-center mb-2 px-1">
              <span
                className="fw-semibold text-dark"
                style={{ fontSize: "0.85rem" }}
              >
                Draw your signature
              </span>
              <small className="text-muted">
                <i className="ti ti-hand-move me-1" />
                Touch or mouse
              </small>
            </div>

            <div
              className="w-100 overflow-hidden rounded-2"
              style={{
                background: "#fff",
                border: "1px solid #e9ecef",
              }}
            >
              <SignatureCanvas
                ref={sigCanvasRef}
                penColor="#1a1a2e"
                canvasProps={{
                  width: 520,
                  height: 150,
                  className: "signature-canvas",
                  style: {
                    width: "100%",
                    height: "150px",
                    display: "block",
                    touchAction: "none",
                  },
                }}
                onEnd={handleDrawEnd}
              />
            </div>

            <div className="d-flex justify-content-between align-items-center mt-2 px-1">
              <small className="text-muted" style={{ fontSize: "0.75rem" }}>
                Sign using your finger or mouse
              </small>

              <button
                type="button"
                onClick={clearSignature}
                disabled={isDisabled}
                className="btn btn-sm text-danger p-0"
              >
                <i className="ti ti-refresh me-1" />
                Clear
              </button>
            </div>
          </div>
        </div>
      )}

      {/* TYPE SIGNATURE */}
      {mode === "type" && !confirmed && (
        <div>
          <label className="form-label fw-semibold mb-2">
            Enter your full name
          </label>

          <input
            type="text"
            className="form-control mb-3"
            placeholder="Type your full name"
            value={typedName}
            onChange={(e) => setTypedName(e.target.value)}
            readOnly={isDisabled}
            style={{
              borderRadius: 8,
              minHeight: 44,
            }}
          />

          <div className="d-flex align-items-center justify-content-between mb-2">
            <span
              className="fw-semibold text-dark"
              style={{ fontSize: "0.85rem" }}
            >
              Choose signature style
            </span>
            <small className="text-muted">
              {SIGNATURE_FONTS.length} styles
            </small>
          </div>

          <div className="d-flex flex-wrap gap-2 mb-3">
            {SIGNATURE_FONTS.map((f) => (
              <button
                key={f.value}
                type="button"
                onClick={() => setSelectedFont(f.value)}
                disabled={isDisabled}
                style={{
                  padding: "7px 14px",
                  borderRadius: 8,
                  cursor: isDisabled ? "not-allowed" : "pointer",
                  fontFamily: f.value,
                  fontSize: "1rem",
                  border: `1.5px solid ${
                    selectedFont === f.value ? "#fd7e14" : "#dee2e6"
                  }`,
                  background: selectedFont === f.value ? "#fff3e8" : "#fff",
                  color: selectedFont === f.value ? "#fd7e14" : "#495057",
                  opacity: isDisabled ? 0.6 : 1,
                }}
              >
                {f.label}
                {selectedFont === f.value && <i className="ti ti-check ms-1" />}
              </button>
            ))}
          </div>

          {/* Signature Preview */}
          <div
            className="rounded-3 p-2 mb-3"
            style={{
              border: "1.5px dashed #ced4da",
              background: "#f8f9fa",
            }}
          >
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span className="fw-semibold" style={{ fontSize: "0.85rem" }}>
                Signature Preview
              </span>
              <small className="text-muted">Live preview</small>
            </div>

            <div
              className="d-flex align-items-center justify-content-center rounded-2"
              style={{
                width: "100%",
                minHeight: 110,
                background: "#fff",
                border: "1px solid #e9ecef",
                overflow: "hidden",
                padding: 10,
              }}
            >
              {typedName ? (
                <span
                  style={{
                    fontFamily: selectedFont,
                    fontSize: "clamp(1.5rem, 6vw, 2.4rem)",
                    color: "#1a1a2e",
                    textAlign: "center",
                    overflowWrap: "anywhere",
                  }}
                >
                  {typedName}
                </span>
              ) : (
                <span className="text-muted" style={{ fontSize: "0.85rem" }}>
                  Your signature will appear here
                </span>
              )}
            </div>
          </div>

          <button
            type="button"
            onClick={handleApplyTyped}
            disabled={!typedName.trim() || isDisabled}
            className="btn w-100 fw-semibold"
            style={{
              background: "#fd7e14",
              color: "#fff",
              borderRadius: 8,
              padding: "10px 16px",
              border: "none",
              opacity: typedName.trim() && !isDisabled ? 1 : 0.5,
            }}
          >
            <i className="ti ti-check me-2" />
            Apply Signature
          </button>
        </div>
      )}

      {/* CONFIRMED SIGNATURE */}
      {confirmed && preview && (
        <div
          className="rounded-3 p-3"
          style={{
            border: "1px solid #b7dfc5",
            background: "#f3fbf5",
          }}
        >
          <div className="d-flex justify-content-between align-items-center mb-2">
            <span className="text-success fw-semibold">
              <i className="ti ti-circle-check me-1" />
              Signature Applied
            </span>
            <button
              type="button"
              onClick={clearSignature}
              className="btn btn-sm btn-outline-danger"
            >
              <i className="ti ti-refresh me-1" />
              Redo
            </button>
          </div>

          <div
            className="rounded-2 bg-white p-2 text-center"
            style={{ border: "1px solid #dee2e6" }}
          >
            <img
              src={preview}
              alt="Confirmed signature"
              style={{
                maxWidth: "100%",
                maxHeight: 80,
                objectFit: "contain",
              }}
            />
          </div>
        </div>
      )}

      {/* EXISTING SAVED SIGNATURE */}
      {!confirmed && !preview && existingSignatureUrl && (
        <div className="mt-3">
          <small className="text-muted fw-semibold">
            Previously saved signature
          </small>

          <div
            className="mt-2 p-2 rounded-2 d-inline-block bg-white"
            style={{ border: "1px solid #dee2e6", maxWidth: "100%" }}
          >
            <img
              src={existingSignatureUrl}
              alt="Saved signature"
              style={{
                maxWidth: "100%",
                maxHeight: 70,
                objectFit: "contain",
              }}
            />
          </div>
        </div>
      )}

      {/* ERROR MESSAGE */}
      {error && (
        <div
          className="alert alert-danger py-2 px-3 mt-3 mb-0"
          style={{ fontSize: "0.82rem", borderRadius: 8 }}
        >
          <i className="ti ti-alert-circle me-2" />
          {error}
        </div>
      )}
    </div>
  );
}

export default ESignaturePad;
